/*
 * Figma `48:26` — Button. Size × Style × State.
 *
 * `state` is not a prop and deliberately so: hover and disabled are the browser's to decide, and a
 * `state="hover"` prop is how a design system ends up with a button that looks hovered and is not.
 * The Figma axis maps to `:hover` and `:disabled` in `ui.css`; what the caller chooses is size and
 * style.
 *
 * `emphasis` is the one addition to the Figma axes, and it is an accessibility contract rather than
 * a look: the brief requires a 44px target for a primary action, and a primary action can be small.
 * It defaults to the value the style implies, so a caller never has to say it twice.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonStyle = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonStyle;
  size?: ButtonSize;
  /** Leading glyph. Decorative — give the button a label, or an `aria-label` if it has none. */
  icon?: ReactNode;
  /** Icon with no visible text. Requires `aria-label`; reserves a 44px square. */
  iconOnly?: boolean;
  emphasis?: 'primary' | 'normal';
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconOnly = false,
  emphasis,
  className = '',
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const resolvedEmphasis = emphasis ?? (variant === 'primary' || variant === 'destructive' ? 'primary' : 'normal');
  return (
    <button
      {...rest}
      type={type}
      className={`jt-btn ${className}`.trim()}
      data-style={variant}
      data-size={size}
      data-emphasis={resolvedEmphasis}
      data-icon-only={iconOnly || undefined}
    >
      {icon}
      {!iconOnly && children}
    </button>
  );
}
